import * as React from 'react';
import type {
  MapStateToProps,
  MapDispatchToProps,
  ThunkAction,
} from 'types/redux';
import {
  FontPicker as ExternalFontPicker,
  Footer,
  FontWeightDropdown,
  Header,
  RecentFonts,
  type FontPickerProps as WixFontPickerProps,
  type FontGroup,
  type FontGroupName,
  type FontOption,
} from '@wix/font-picker';

import type { EditorAPI } from '@/editorAPI';
import * as util from '@/util';
import * as stateManagement from '@/stateManagement';

import { getFontGroups } from './getFontGroups';

export { type FontGroup, type FontGroupName, type FontOption };

export type Origin = 'keyboard' | 'mouse' | undefined;

interface FontPickerStateProps {
  fontsGroups: FontGroup[];
  recentFonts: FontOption[];
}

interface FontPickerDispatchProps {
  onRecentFontChange: (fonts: FontOption[]) => void;
  onAddLanguagesClick: () => void;
  onUploadFontsClick: () => void;
}

interface FontPickerOwnProps {
  family: string;
  weight: number;
  showWeightPicker?: boolean;
  getFontSuffix: WixFontPickerProps['getFontSuffix'];
  onChange: (family: string, weight: number | undefined) => void;
  onPreview?: (
    family: string,
    weight: number | undefined,
    origin: Origin,
  ) => void;
  onToggle?: () => void;
}

type FontPickerProps = FontPickerOwnProps &
  FontPickerDispatchProps &
  FontPickerStateProps;

const recentFontsKey = 'site-recent-fonts';

const FontPickerPure: React.FC<FontPickerProps> = (props) => {
  const {
    family,
    weight,
    fontsGroups,
    showWeightPicker = true,
    recentFonts,
    getFontSuffix,
    onChange,
    onPreview,
    onToggle,
    onRecentFontChange,
    onAddLanguagesClick,
    onUploadFontsClick,
  } = props;

  const handleRecentFontsChange = React.useCallback(
    (fonts: FontOption[]) => {
      onRecentFontChange(fonts);
    },
    [onRecentFontChange],
  );

  const handlePreview: WixFontPickerProps['onPreview'] = React.useCallback(
    (family, weight, biData) => {
      if (onPreview) {
        onPreview(family, weight, biData?.fontItemNavigationOrigin);
      }
    },
    [onPreview],
  );

  const handleChange: WixFontPickerProps['onChange'] = React.useCallback(
    (family, weight) => {
      onChange(family, weight);
    },
    [onChange],
  );

  const weightsDropdown = showWeightPicker ? <FontWeightDropdown /> : undefined;

  return (
    <ExternalFontPicker
      family={family}
      weight={weight}
      fontsGroups={fontsGroups}
      getFontSuffix={getFontSuffix}
      header={<Header />}
      recentFontsSection={
        <RecentFonts
          fonts={recentFonts}
          onFontsChange={handleRecentFontsChange}
        />
      }
      footer={
        <Footer
          onAddLanguagesClick={onAddLanguagesClick}
          onUploadFontsClick={onUploadFontsClick}
        />
      }
      weightDropdown={weightsDropdown}
      onPickerToggle={onToggle}
      onChange={handleChange}
      onPreview={handlePreview}
    />
  );
};

const getEditorAPI: ThunkAction = (_dispatch, _getState, { editorAPI }) =>
  editorAPI;

const mapStateToProps: MapStateToProps<
  FontPickerStateProps,
  FontPickerOwnProps
> = ({ editorAPI }) => {
  const recentFonts =
    stateManagement.userPreferences.selectors.getSiteUserPreferences(
      recentFontsKey,
    )(editorAPI.store.getState()) ?? [];
  return {
    fontsGroups: getFontGroups(editorAPI),
    recentFonts,
  };
};

const mapDispatchToProps: MapDispatchToProps<
  FontPickerDispatchProps,
  FontPickerOwnProps
> = (dispatch) => {
  const editorAPI: EditorAPI = dispatch(getEditorAPI);

  return {
    onRecentFontChange: (fonts: FontOption[]): void => {
      editorAPI.store.dispatch(
        stateManagement.userPreferences.actions.setSiteUserPreferences(
          recentFontsKey,
          fonts,
        ),
      );
    },
    onAddLanguagesClick: () => {
      editorAPI.panelManager.openPanel(
        'panels.toolPanels.languageSupport',
        {},
        true,
      );
    },
    onUploadFontsClick: () => {
      editorAPI.panelManager.openPanel(
        'panels.toolPanels.fontsUploadPanel',
        {},
        true,
      );
    },
  };
};

const FontPickerConnected = util.hoc.connect(
  util.hoc.STORES.EDITOR_API,
  mapStateToProps,
  mapDispatchToProps,
)(FontPickerPure);

export { FontPickerConnected as FontPicker };
